import { useState } from 'react';
import { Button, Label, Modal, TextInput, Datepicker, Select } from "flowbite-react";
import usePublicationController from '../controllers/usePublicationController';

function Publication() {
  const { 
    publications, 
    loading, 
    error, 
    addPublication, 
    updatePublication, 
    deletePublication
  } = usePublicationController();

  const [openModal, setOpenModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentPublication, setCurrentPublication] = useState(null);
  const [pubDate, setPubDate] = useState(null);
  const [publicationData, setPublicationData] = useState({
    title: '',
    type: 'book',
    publisher: '',
    url: ''
  });

  function onCloseModal() {
    setOpenModal(false);
    setIsEditMode(false);
    setCurrentPublication(null);
    setPubDate(new Date());
    setPublicationData({
      title: '',
      type: 'book',
      publisher: '',
      url: ''
    });
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPublicationData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateChange = (date) => {
    setPubDate(date);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formattedData = {
        ...publicationData,
        date: pubDate.toISOString().split('T')[0] // Format YYYY-MM-DD
      };

      if (isEditMode && currentPublication) {
        await updatePublication(currentPublication.id, formattedData);
      } else {
        await addPublication(formattedData);
      }
      onCloseModal();
    } catch (err) {
      console.error("Operation failed:", err);
    }
  };

  const handleEdit = (publication) => {
    setCurrentPublication(publication);
    setPublicationData({
      title: publication.title,
      type: publication.type,
      publisher: publication.publisher,
      url: publication.url || ''
    });

    let parsedDate;
    if (publication.date) {
      if (typeof publication.date === 'string' && publication.date.includes('T')) {
        parsedDate = new Date(publication.date);
      } 
      else if (typeof publication.date === 'string') {
        const [year, month, day] = publication.date.split('-');
        parsedDate = new Date(year, month - 1, day);
      }
      else if (publication.date instanceof Date) {
        parsedDate = publication.date;
      }
    }
    setPubDate(parsedDate || new Date());
    setIsEditMode(true);
    setOpenModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this publication?')) {
      await deletePublication(id);
    }
  };

  return (
    <div className='flex'>
      <section className="flex-1 py-5 px-5 bg-gray-100 dark:bg-gray-900">
        <div className="grid md:grid-cols-3 grid-cols-2">
          <div className='my-6 md:col-span-2 md:pl-6'>
            <h1 className="text-3xl font-extrabold text-left lg:text-5xl">
              <span className="text-transparent bg-gradient-to-br bg-clip-text from-sky-800 via-sky-600 to-sky-700">
                Publication
              </span>
            </h1>
            <p className="max-w-3xl mx-auto mt-6 mr-4 text-sm text-left text-gray-700 dark:text-white md:text-md">
              Share your knowledge! Document your published works and research contributions.
            </p>
          </div>
          
          <div className="mx-auto my-auto col-span-1">
            <Button color="blue" onClick={() => setOpenModal(true)}>
              <b>+</b> Add Your Publication
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap gap-4">
          {loading && <p>Loading publications...</p>}
          {error && <p className="text-red-500">{error}</p>}
          
          {publications.map(pub => (
            <div key={pub.id} className="w-full max-w-sm overflow-hidden bg-white rounded-xl shadow-lg dark:bg-gray-800">
              <div className="px-6 py-4 border-l-4 border-purple-500">
                <div className="flex items-center mb-3">
                  <svg className="w-6 h-6 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                  </svg>
                  <h1 className="text-xl font-bold text-gray-800 dark:text-white">{pub.title}</h1>
                </div>
            
                <div className="space-y-2 text-sm">
                  <div className="flex items-center">
                    <span className="w-24 font-medium text-gray-500 dark:text-gray-400">Type</span>
                    <span className="px-2 py-1 text-sm bg-purple-100 dark:bg-purple-900 rounded-lg">: {pub.type}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="w-24 font-medium text-gray-500 dark:text-gray-400">Publisher</span>
                    <span className="text-gray-700 dark:text-gray-300">: {pub.publisher}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="w-24 font-medium text-gray-500 dark:text-gray-400">Published</span>
                    <span className="text-gray-700 dark:text-gray-300">: 
                      {new Date(pub.date).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </span>
                  </div>
                </div>
            
                {pub.url && (
                  <div className="mt-4">
                    <a href={pub.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-blue-600 hover:text-blue-800 dark:text-blue-400">
                      <span>View Publication</span>
                      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </div>
                )}
                
                <div className="flex justify-end space-x-2 mt-4">
                  <Button color="gray" onClick={() => handleEdit(pub)}>
                    Edit
                  </Button>
                  <Button color="red" onClick={() => handleDelete(pub.id)}>
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <Modal show={openModal} onClose={onCloseModal} popup>
          <Modal.Header />
          <Modal.Body>
            <form onSubmit={handleSubmit} className="space-y-6 p-5">
              <h3 className="text-xl font-medium text-gray-900 dark:text-white">
                {isEditMode ? 'Edit Publication' : 'Add Your Publication'}
              </h3>
              
              <div>
                <Label htmlFor="title">Title</Label>
                <TextInput 
                  id="title" 
                  name="title" 
                  value={publicationData.title} 
                  onChange={handleInputChange} 
                  placeholder="Cyber Security Book" 
                  required 
                />
              </div>

              <div>
                <Label htmlFor="type">Type of Publication</Label>
                <Select 
                  id="type" 
                  name="type" 
                  value={publicationData.type} 
                  onChange={handleInputChange} 
                  required
                >
                  <option value="book">Book</option>
                  <option value="journal">Journal</option>
                  <option value="paper">Paper</option>
                  <option value="article">Article</option>
                  <option value="report">Report</option>
                  <option value="others">Others</option>
                </Select>
              </div>

              <div>
                <Label htmlFor="publisher">Publisher</Label>
                <TextInput 
                  id="publisher" 
                  name="publisher" 
                  value={publicationData.publisher} 
                  onChange={handleInputChange} 
                  placeholder="Wiley" 
                  required 
                />
              </div>

              <div>
                <Label htmlFor="pubDate">Publication Date</Label>
                <Datepicker 
                  id="pubDate"
                  selected={pubDate} 
                  onChange={(date) => setPubDate(date)}
                  required
                />
              </div>

              <div>
                <Label htmlFor="url">Publication URL</Label>
                <TextInput 
                  id="url" 
                  name="url" 
                  value={publicationData.url} 
                  onChange={handleInputChange} 
                  placeholder="https://example.com/mybook" 
                />
              </div>

              <div className="w-full">
                <Button type="submit" color="blue">
                  {isEditMode ? 'Update Publication' : 'Add Publication'}
                </Button>
              </div>
            </form>
          </Modal.Body>
        </Modal>
      </section>
    </div>
  );
}

export default Publication;